import { useEffect, useMemo, useState } from 'react';

import { stringParser, useSearchParam } from '#hooks/shared/useSearchParam.ts';

export const SEARCH_DEBOUNCE_TIME = 300;

export const useDebouncedSearch = () => {
    const [search, setSearch, resetSearch] = useSearchParam('search', stringParser.default(''));
    const [searchInput, setSearchInput] = useState(search);

    useEffect(() => {
        setSearchInput(search);
    }, [search]);

    const debouncedSearch = useMemo(() => {
        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        return {
            run: (input: string) => {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }

                timeoutId = setTimeout(() => {
                    input === '' ? resetSearch() : setSearch(input);
                }, SEARCH_DEBOUNCE_TIME);
            },
            cancel: () => {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                    timeoutId = undefined;
                }
            },
        };
    }, [resetSearch, setSearch]);

    useEffect(
        () => () => {
            debouncedSearch.cancel();
        },
        [debouncedSearch]
    );

    const onSearch = (input: string) => {
        setSearchInput(input);
        debouncedSearch.run(input);
    };

    return { search, searchInput, onSearch };
};
